import { db } from '../firebase';
import { collection, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, addDoc, query, where, orderBy, onSnapshot } from 'firebase/firestore';

// Check if Firebase is actually configured with user's keys
const isFirebaseConfigured = () => {
  try {
    const config = db?.app?.options;
    return config && config.apiKey && config.apiKey !== "YOUR_API_KEY";
  } catch (e) {
    return false;
  }
};

export const firebaseEnabled = isFirebaseConfigured();

if (firebaseEnabled) {
  console.log("🔥 Firebase backend is successfully configured and active.");
} else {
  console.log("ℹ️ Firebase is not configured. Running in high-fidelity mock/localStorage mode.");
}

export const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`[localStorage] Unable to save "${key}" (quota likely exceeded):`, e);
  }
};

// ─── REAL-TIME SYNC HELPERS ──────────────────────────────────────────────────
export const notifyProductsUpdated = (updatedProducts) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_products_updated', { detail: updatedProducts }));
  } catch (e) {}
};

export const notifyReviewsUpdated = (updatedReviews) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_reviews_updated', { detail: updatedReviews }));
  } catch (e) {}
};

export const notifyOrdersUpdated = (updatedOrders) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_orders_updated', { detail: updatedOrders }));
  } catch (e) {}
};

export const notifyHomeContentUpdated = (updatedSettings) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_homecontent_updated', { detail: updatedSettings }));
  } catch (e) {}
};

export const notifyCollectionsDataUpdated = (updatedSettings) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_collectionsdata_updated', { detail: updatedSettings }));
  } catch (e) {}
};

export const notifyAppointmentsUpdated = (updatedAppointments) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_appointments_updated', { detail: updatedAppointments }));
  } catch (e) {}
};

export const notifyDiscountsUpdated = (updatedDiscounts) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_discounts_updated', { detail: updatedDiscounts }));
  } catch (e) {}
};

export const notifyNewsletterUpdated = (updatedNewsletter) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_newsletter_updated', { detail: updatedNewsletter }));
  } catch (e) {}
};

export const notifyWishlistUpdated = (updatedWishlist) => {
  try {
    window.dispatchEvent(new CustomEvent('hok_wishlist_updated', { detail: updatedWishlist }));
  } catch (e) {}
};

export const triggerStockLimitAlert = (quantity) => {
  const q = quantity !== undefined ? quantity : 0;
  try {
    window.dispatchEvent(new CustomEvent('hok_stock_limit_alert', {
      detail: {
        message: `Due to high demand we can only fulfill ( ${q} ) for now.`
      }
    }));
  } catch (e) {}
};

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
const recordDeletedProducts = (ids) => {
  try {
    const existing = JSON.parse(localStorage.getItem('hok_deleted_products') || '[]');
    const set = new Set([...existing, ...ids]);
    safeSetItem('hok_deleted_products', JSON.stringify(Array.from(set)));
  } catch (e) {
    console.warn("Could not record deleted product ids locally:", e);
  }
};

export const resolveMediaUrl = (url) => {
  if (typeof url !== 'string' || !url) return '';

  // Normalize leading relative paths
  let cleanUrl = url.trim();
  if (cleanUrl.startsWith('./uploads/')) cleanUrl = cleanUrl.slice(1);
  if (cleanUrl.startsWith('uploads/')) cleanUrl = '/' + cleanUrl;
  if (cleanUrl.startsWith('http://localhost:3000/uploads/')) {
    cleanUrl = cleanUrl.replace('http://localhost:3000', '');
  }
  if (cleanUrl.startsWith('http://127.0.0.1:3000/uploads/')) {
    cleanUrl = cleanUrl.replace('http://127.0.0.1:3000', '');
  }

  // Any /uploads/ path must resolve to the live media host where user uploaded assets are stored
  if (cleanUrl.startsWith('/uploads/')) {
    return 'https://houseofkarnnikaraa.com' + cleanUrl;
  }
  return cleanUrl;
};

const sanitizeUrls = (p) => {
  if (!p) return p;

  const fix = (url) => {
    return resolveMediaUrl(url);
  };

  const primaryImage = fix(p.image || (Array.isArray(p.images) && p.images[0]) || '');
  const fixedImages = Array.isArray(p.images) && p.images.length > 0
    ? p.images.map(fix).filter(Boolean)
    : [
        primaryImage,
        fix(p.image2),
        fix(p.image3),
        fix(p.image4)
      ].filter(Boolean);

  return {
    ...p,
    image: primaryImage || (fixedImages[0] || ''),
    images: fixedImages.length > 0 ? fixedImages : (primaryImage ? [primaryImage] : []),
    image2: fix(p.image2 || fixedImages[1] || ''),
    image3: fix(p.image3 || fixedImages[2] || ''),
    image4: fix(p.image4 || fixedImages[3] || '')
  };
};

export const isMockProduct = (p) => {
  if (!p || !p.id) return true;
  const strId = String(p.id).toLowerCase().trim();
  // Reject explicit mock, dummy, sample, demo, catalog items
  if (/^(ring|earring|necklace|pendant|bracelet|bangle|set|item|catalog|mock|demo|dummy|sample)-\d+$/i.test(strId)) return true;
  if (strId.startsWith('mock-') || strId.startsWith('demo-') || strId.startsWith('dummy-') || strId.startsWith('sample-')) return true;
  if (p.isMock === true || p.isDemo === true || p.isSample === true) return true;

  // Real manually uploaded products from admin are strictly prefixed with 'prod-'
  if (!strId.startsWith('prod-')) {
    return true;
  }
  return false;
};

export const getProductTimestamp = (p) => {
  if (!p) return 0;
  if (p.createdAt) {
    if (typeof p.createdAt === 'object' && typeof p.createdAt.seconds === 'number') {
      return p.createdAt.seconds * 1000;
    }
    if (typeof p.createdAt === 'number' && !isNaN(p.createdAt)) {
      return p.createdAt;
    }
    const t = new Date(p.createdAt).getTime();
    if (!isNaN(t) && t > 0) return t;
  }
  const digits = String(p.id || '').replace(/\D/g, '');
  if (digits.length >= 10) {
    const num = parseInt(digits, 10);
    if (!isNaN(num) && num > 0) return num;
  }
  return 0;
};

export const subscribeProducts = (callback, fallbackProducts) => {
  const getCleanList = (prods) => {
    const isCleared = localStorage.getItem('hok_products_cleared') === 'true';
    if (isCleared) return [];
    const deletedIds = (() => {
      try {
        return new Set(JSON.parse(localStorage.getItem('hok_deleted_products') || '[]'));
      } catch (e) {
        return new Set();
      }
    })();
    return (prods || [])
      .filter(p => !isMockProduct(p))
      .filter(p => !deletedIds.has(p.id))
      .map(sanitizeUrls)
      .sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
  };

  // Immediate callback from local storage / fallback (purging mock items)
  try {
    const local = localStorage.getItem('hok_products');
    if (local) {
      const parsed = JSON.parse(local);
      const cleaned = (parsed || []).filter(p => !isMockProduct(p));
      if (cleaned.length !== (parsed || []).length) {
        safeSetItem('hok_products', JSON.stringify(cleaned));
      }
      callback(getCleanList(cleaned));
    } else if (fallbackProducts) {
      const cleanedFallback = (fallbackProducts || []).filter(p => !isMockProduct(p));
      callback(getCleanList(cleanedFallback));
    }
  } catch (e) {}

  // Local event listener for cross-component and same-tab instant sync
  const handleLocalUpdate = (e) => {
    if (e.detail) {
      callback(getCleanList(e.detail));
    }
  };
  window.addEventListener('hok_products_updated', handleLocalUpdate);

  const handleStorageUpdate = (e) => {
    if (e.key === 'hok_products' || e.key === 'hok_deleted_products' || e.key === 'hok_products_cleared') {
      try {
        const local = localStorage.getItem('hok_products');
        if (local) callback(getCleanList(JSON.parse(local)));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorageUpdate);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'products');
      unsubscribeFirestore = onSnapshot(colRef, (snap) => {
        const isCleared = localStorage.getItem('hok_products_cleared') === 'true';
        if (isCleared) {
          callback([]);
          return;
        }
        const deletedIds = (() => {
          try {
            return new Set(JSON.parse(localStorage.getItem('hok_deleted_products') || '[]'));
          } catch (e) {
            return new Set();
          }
        })();
        const live = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => !isMockProduct(p))
          .filter(p => !deletedIds.has(p.id))
          .map(sanitizeUrls)
          .sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
          
        safeSetItem('hok_products', JSON.stringify(live));
        callback(live);
      }, (error) => {
        console.warn("Firestore products onSnapshot warning:", error);
      });
    } catch (err) {
      console.warn("Firestore subscription initialization error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_products_updated', handleLocalUpdate);
    window.removeEventListener('storage', handleStorageUpdate);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getProducts = async (fallbackProducts) => {
  const isCleared = localStorage.getItem('hok_products_cleared') === 'true';
  const deletedIds = (() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('hok_deleted_products') || '[]'));
    } catch (e) {
      return new Set();
    }
  })();

  if (isCleared) {
    try {
      const local = localStorage.getItem('hok_products');
      if (local) return JSON.parse(local);
    } catch (e) {}
    return [];
  }

  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_products');
    if (local) {
      return JSON.parse(local).filter(p => !isMockProduct(p)).filter(p => !deletedIds.has(p.id)).map(sanitizeUrls);
    }
    const filtered = (fallbackProducts || []).filter(p => !isMockProduct(p)).filter(p => !deletedIds.has(p.id)).map(sanitizeUrls);
    safeSetItem('hok_products', JSON.stringify(filtered));
    return filtered;
  }

  try {
    const colRef = collection(db, 'products');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      if (isCleared) {
        return [];
      }
      return [];
    }
    const liveProds = snap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => !isMockProduct(p))
      .filter(p => !deletedIds.has(p.id))
      .map(sanitizeUrls);
    safeSetItem('hok_products', JSON.stringify(liveProds));
    return liveProds;
  } catch (error) {
    console.error("Error fetching products from Firebase (using local):", error);
    const local = localStorage.getItem('hok_products');
    if (local) return JSON.parse(local).filter(p => !isMockProduct(p)).filter(p => !deletedIds.has(p.id)).map(sanitizeUrls);
    return (fallbackProducts || []).filter(p => !isMockProduct(p)).filter(p => !deletedIds.has(p.id)).map(sanitizeUrls);
  }
};

export const saveProduct = async (product) => {
  const prodWithTime = {
    ...product,
    createdAt: product.createdAt || new Date().toISOString()
  };

  // Reset cleared flag if adding a product
  try {
    localStorage.removeItem('hok_products_cleared');
    const existingDeleted = JSON.parse(localStorage.getItem('hok_deleted_products') || '[]');
    const cleanedDeleted = existingDeleted.filter(id => id !== prodWithTime.id);
    safeSetItem('hok_deleted_products', JSON.stringify(cleanedDeleted));
  } catch (e) {}

  // 1. Always update local storage
  try {
    const local = localStorage.getItem('hok_products');
    const products = local ? JSON.parse(local) : [];
    const updated = products.some(p => p.id === prodWithTime.id)
      ? products.map(p => p.id === prodWithTime.id ? prodWithTime : p)
      : [prodWithTime, ...products];
    const sorted = [...updated].sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
    safeSetItem('hok_products', JSON.stringify(sorted));
    notifyProductsUpdated(sorted);
  } catch (e) {
    console.warn("Local storage save error:", e);
  }

  // 2. Persist to Firebase
  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'products', prodWithTime.id), prodWithTime);
    } catch (error) {
      console.warn("Firebase saveProduct error (saved locally):", error);
    }
  }
  return prodWithTime;
};

export const deleteProduct = async (productId) => {
  // 1. Immediately remove from local storage
  try {
    const local = localStorage.getItem('hok_products');
    if (local) {
      const products = JSON.parse(local).filter(p => p.id !== productId);
      safeSetItem('hok_products', JSON.stringify(products));
      notifyProductsUpdated(products);
    }
    recordDeletedProducts([productId]);
  } catch (e) {
    console.warn("Local storage delete error:", e);
  }

  // 2. Remove from Firebase if enabled
  if (firebaseEnabled) {
    try {
      await deleteDoc(doc(db, 'products', productId));
      console.log(`Product ${productId} deleted from Firebase.`);
    } catch (error) {
      console.warn("Firebase deleteDoc encountered an error (deleted locally):", error);
    }
  }
  return { success: true, id: productId };
};

export const deleteMultipleProducts = async (productIds) => {
  if (!productIds || productIds.length === 0) return { success: true, count: 0 };
  const idSet = new Set(productIds);

  // 1. Immediately remove from local storage
  try {
    const local = localStorage.getItem('hok_products');
    if (local) {
      const products = JSON.parse(local).filter(p => !idSet.has(p.id));
      safeSetItem('hok_products', JSON.stringify(products));
      notifyProductsUpdated(products);
    }
    recordDeletedProducts(productIds);
  } catch (e) {
    console.warn("Local storage batch delete error:", e);
  }

  // 2. Remove from Firebase if enabled
  if (firebaseEnabled) {
    try {
      const promises = productIds.map(id => deleteDoc(doc(db, 'products', id)));
      await Promise.allSettled(promises);
      console.log(`${productIds.length} products batch deleted from Firebase.`);
    } catch (error) {
      console.warn("Firebase batch delete error (deleted locally):", error);
    }
  }
  return { success: true, count: productIds.length };
};

export const deleteAllProducts = async () => {
  // 1. Immediately clear local storage and set cleared flag
  try {
    safeSetItem('hok_products', JSON.stringify([]));
    safeSetItem('hok_products_cleared', 'true');
    notifyProductsUpdated([]);
  } catch (e) {
    console.warn("Local storage delete-all error:", e);
  }

  // 2. Clear Firebase products collection
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'products');
      const snap = await getDocs(colRef);
      if (!snap.empty) {
        const promises = snap.docs.map(d => deleteDoc(doc(db, 'products', d.id)));
        await Promise.allSettled(promises);
      }
      console.log("All products deleted from Firebase.");
    } catch (error) {
      console.warn("Firebase deleteAll error (cleared locally):", error);
    }
  }
  return { success: true };
};

// ─── ORDERS ──────────────────────────────────────────────────────────────────
export const subscribeOrders = (callback, fallbackOrders = []) => {
  try {
    const local = localStorage.getItem('hok_orders');
    if (local) {
      callback(JSON.parse(local));
    } else if (fallbackOrders && fallbackOrders.length > 0) {
      callback(fallbackOrders);
    }
  } catch (e) {}

  const handleLocalOrders = (e) => {
    if (e.detail) callback(e.detail);
  };
  window.addEventListener('hok_orders_updated', handleLocalOrders);

  const handleStorageOrders = (e) => {
    if (e.key === 'hok_orders') {
      try {
        const local = localStorage.getItem('hok_orders');
        if (local) callback(JSON.parse(local));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorageOrders);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'orders');
      unsubscribeFirestore = onSnapshot(colRef, (snap) => {
        const live = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (live.length > 0) {
          safeSetItem('hok_orders', JSON.stringify(live));
          callback(live);
        }
      }, (err) => {
        console.warn("Firestore orders onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore orders subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_orders_updated', handleLocalOrders);
    window.removeEventListener('storage', handleStorageOrders);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getOrders = async (fallbackOrders) => {
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_orders');
    return local ? JSON.parse(local) : fallbackOrders;
  }
  try {
    const colRef = collection(db, 'orders');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      return fallbackOrders;
    }
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching orders from Firebase:", error);
    return fallbackOrders;
  }
};

export const saveOrder = async (order) => {
  try {
    const local = localStorage.getItem('hok_orders');
    const orders = local ? JSON.parse(local) : [];
    const updated = orders.some(o => o.id === order.id)
      ? orders.map(o => o.id === order.id ? order : o)
      : [order, ...orders];
    safeSetItem('hok_orders', JSON.stringify(updated));
    notifyOrdersUpdated(updated);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'orders', order.id), order);
    } catch (error) {
      console.warn("Firebase saveOrder warning (persisted locally):", error);
    }
  }
  return order;
};

export const deductStockForOrder = async (orderItems = []) => {
  if (!orderItems || orderItems.length === 0) return [];

  try {
    const local = localStorage.getItem('hok_products');
    let products = local ? JSON.parse(local) : [];

    const orderMap = {};
    orderItems.forEach(item => {
      if (item && item.id) {
        const idStr = String(item.id);
        orderMap[idStr] = (orderMap[idStr] || 0) + (Number(item.qty) || 1);
      }
    });

    // Ensure all ordered items exist in the products list so their stock is tracked
    orderItems.forEach(item => {
      if (item && item.id && !products.some(p => String(p.id) === String(item.id))) {
        products.push({
          ...item,
          stock: typeof item.stock === 'number' ? item.stock : 10
        });
      }
    });

    const updatedProducts = products.map(p => {
      const idStr = String(p.id);
      if (orderMap[idStr] !== undefined) {
        const currentStock = typeof p.stock === 'number' ? p.stock : 10;
        const newStock = Math.max(0, currentStock - orderMap[idStr]);
        return { ...p, stock: newStock };
      }
      return p;
    });

    safeSetItem('hok_products', JSON.stringify(updatedProducts));
    notifyProductsUpdated(updatedProducts);

    if (firebaseEnabled) {
      for (const item of orderItems) {
        if (!item || !item.id) continue;
        try {
          const targetProd = updatedProducts.find(p => String(p.id) === String(item.id));
          if (targetProd) {
            const docRef = doc(db, 'products', String(item.id));
            await updateDoc(docRef, { stock: targetProd.stock });
          }
        } catch (err) {
          console.warn(`Stock deduction for ${item.id} saved locally:`, err);
        }
      }
    }
    return updatedProducts;
  } catch (e) {
    console.error("Error in deductStockForOrder:", e);
    return [];
  }
};

// ─── REVIEWS ─────────────────────────────────────────────────────────────────
export const subscribeReviews = (callback, fallbackReviews) => {
  try {
    const local = localStorage.getItem('hok_reviews');
    if (local) {
      callback(JSON.parse(local));
    } else if (fallbackReviews) {
      callback(fallbackReviews);
    }
  } catch (e) {}

  const handleLocalReviewUpdate = (e) => {
    if (e.detail) callback(e.detail);
  };
  window.addEventListener('hok_reviews_updated', handleLocalReviewUpdate);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'reviews');
      unsubscribeFirestore = onSnapshot(colRef, (snap) => {
        const live = snap.docs.map(d => ({ id: d.id, ...docDataWithId(d) }));
        if (live.length > 0) {
          safeSetItem('hok_reviews', JSON.stringify(live));
          callback(live);
        }
      }, (err) => {
        console.warn("Firestore reviews onSnapshot warning:", err);
      });
    } catch (e) {}
  }

  return () => {
    window.removeEventListener('hok_reviews_updated', handleLocalReviewUpdate);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

const docDataWithId = (docSnap) => ({ id: docSnap.id, ...docSnap.data() });

export const getReviews = async (fallbackReviews) => {
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_reviews');
    return local ? JSON.parse(local) : fallbackReviews;
  }
  try {
    const colRef = collection(db, 'reviews');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      return fallbackReviews;
    }
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching reviews from Firebase:", error);
    return fallbackReviews;
  }
};

export const saveReview = async (review) => {
  try {
    const local = localStorage.getItem('hok_reviews');
    const reviews = local ? JSON.parse(local) : [];
    const updated = [review, ...reviews];
    safeSetItem('hok_reviews', JSON.stringify(updated));
    notifyReviewsUpdated(updated);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      const docRef = await addDoc(collection(db, 'reviews'), review);
      return { id: docRef.id, ...review };
    } catch (error) {
      console.warn("Firebase saveReview warning (persisted locally):", error);
    }
  }
  return review;
};

export const updateReviewService = async (id, status) => {
  try {
    const local = localStorage.getItem('hok_reviews');
    const reviews = local ? JSON.parse(local) : [];
    const updated = reviews.map(r => r.id === id ? { ...r, status } : r);
    safeSetItem('hok_reviews', JSON.stringify(updated));
    notifyReviewsUpdated(updated);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      const docRef = doc(db, 'reviews', String(id));
      await updateDoc(docRef, { status });
    } catch (error) {
      console.warn("Firebase updateReview warning (persisted locally):", error);
    }
  }
};

export const deleteReviewService = async (id) => {
  try {
    const local = localStorage.getItem('hok_reviews');
    const reviews = local ? JSON.parse(local) : [];
    const updated = reviews.filter(r => r.id !== id);
    safeSetItem('hok_reviews', JSON.stringify(updated));
    notifyReviewsUpdated(updated);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      const docRef = doc(db, 'reviews', String(id));
      await deleteDoc(docRef);
    } catch (error) {
      console.warn("Firebase deleteReview warning (persisted locally):", error);
    }
  }
};

// ─── APPOINTMENTS ────────────────────────────────────────────────────────────
export const subscribeAppointments = (callback, fallbackAppointments = []) => {
  try {
    const local = localStorage.getItem('hok_appointments');
    if (local) {
      callback(JSON.parse(local));
    } else if (fallbackAppointments && fallbackAppointments.length > 0) {
      callback(fallbackAppointments);
    }
  } catch (e) {}

  const handleLocal = (e) => {
    if (e.detail) callback(e.detail);
  };
  window.addEventListener('hok_appointments_updated', handleLocal);
  window.addEventListener('hok_appointment_booked', () => {
    try {
      const local = localStorage.getItem('hok_appointments');
      if (local) callback(JSON.parse(local));
    } catch (err) {}
  });

  const handleStorage = (e) => {
    if (e.key === 'hok_appointments') {
      try {
        const local = localStorage.getItem('hok_appointments');
        if (local) callback(JSON.parse(local));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorage);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'appointments');
      unsubscribeFirestore = onSnapshot(colRef, (snap) => {
        const live = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (live.length > 0) {
          safeSetItem('hok_appointments', JSON.stringify(live));
          callback(live);
        }
      }, (err) => {
        console.warn("Firestore appointments onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore appointments subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_appointments_updated', handleLocal);
    window.removeEventListener('storage', handleStorage);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getAppointments = async (fallbackAppointments) => {
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_appointments');
    return local ? JSON.parse(local) : fallbackAppointments;
  }
  try {
    const colRef = collection(db, 'appointments');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      return fallbackAppointments;
    }
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching appointments from Firebase:", error);
    return fallbackAppointments;
  }
};

export const saveAppointment = async (appointment) => {
  try {
    const local = localStorage.getItem('hok_appointments');
    const appointments = local ? JSON.parse(local) : [];
    const updated = appointments.some(a => a.id === appointment.id)
      ? appointments.map(a => a.id === appointment.id ? appointment : a)
      : [appointment, ...appointments];
    safeSetItem('hok_appointments', JSON.stringify(updated));
    notifyAppointmentsUpdated(updated);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'appointments', appointment.id), appointment);
    } catch (error) {
      console.warn("Firebase saveAppointment warning (persisted locally):", error);
    }
  }
  return appointment;
};

// ─── HOMEPAGE SETTINGS ───────────────────────────────────────────────────────
export const subscribeHomepageSettings = (callback, fallbackSettings) => {
  const sanitize = (settings) => {
    if (!settings) return settings;
    const cleanStr = (url) => {
      if (typeof url !== 'string') return url;
      if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && url.startsWith('/uploads/')) {
        return 'https://houseofkarnnikaraa.com' + url;
      }
      return url.replace(/^https?:\/\/[^\/]+\/uploads\//, '/uploads/');
    };
    const res = {
      ...settings,
      heroImage: cleanStr(settings.heroImage),
      mobileHeroImage: cleanStr(settings.mobileHeroImage || ''),
      calloutVideo: cleanStr(settings.calloutVideo)
    };
    if (res.calloutVideo === '/jewelry_video.mp4') {
      res.calloutVideo = '';
    }
    return res;
  };

  try {
    const local = localStorage.getItem('hok_homeContent');
    if (local) {
      const parsed = JSON.parse(local);
      // If local storage was contaminated with an oversized Base64 string, purge it immediately
      if (parsed?.heroImage?.length > 10000 || parsed?.mobileHeroImage?.length > 10000) {
        localStorage.removeItem('hok_homeContent');
        if (fallbackSettings) callback(sanitize(fallbackSettings));
      } else {
        callback(sanitize(parsed));
      }
    } else if (fallbackSettings) {
      callback(sanitize(fallbackSettings));
    }
  } catch (e) {
    if (fallbackSettings) callback(sanitize(fallbackSettings));
  }

  const handleLocal = (e) => {
    if (e.detail) callback(sanitize(e.detail));
  };
  window.addEventListener('hok_homecontent_updated', handleLocal);

  const handleStorage = (e) => {
    if (e.key === 'hok_homeContent') {
      try {
        const local = localStorage.getItem('hok_homeContent');
        if (local) callback(sanitize(JSON.parse(local)));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorage);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const docRef = doc(db, 'settings', 'homepage');
      unsubscribeFirestore = onSnapshot(docRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const clean = sanitize(data);
          safeSetItem('hok_homeContent', JSON.stringify(clean));
          callback(clean);
        }
      }, (err) => {
        console.warn("Firestore homepage settings onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore homepage subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_homecontent_updated', handleLocal);
    window.removeEventListener('storage', handleStorage);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getHomepageSettings = async (fallbackSettings) => {
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_homeContent');
    return local ? JSON.parse(local) : fallbackSettings;
  }
  try {
    const snap = await getDocs(collection(db, 'settings'));
    const docObj = snap.docs.find(d => d.id === 'homepage');
    if (!docObj) {
      await setDoc(doc(db, 'settings', 'homepage'), fallbackSettings);
      return fallbackSettings;
    }
    return docObj.data();
  } catch (error) {
    console.error("Error fetching homepage settings from Firebase:", error);
    return fallbackSettings;
  }
};

export const saveHomepageSettings = async (settings) => {
  // Validate that huge raw base64 data URLs (>300KB) are not sent to Firestore
  if (settings?.heroImage?.startsWith('data:') && settings.heroImage.length > 300000) {
    throw new Error('Desktop hero image contains an uncompressed raw preview that exceeds Firestore\'s 1MB limit. Please upload using "Upload High-Res Hero Image" so it saves to the server.');
  }
  if (settings?.mobileHeroImage?.startsWith('data:') && settings.mobileHeroImage.length > 300000) {
    throw new Error('Mobile hero image contains an uncompressed raw preview that exceeds Firestore\'s 1MB limit. Please upload using the crop tool so it saves to the server.');
  }

  try {
    safeSetItem('hok_homeContent', JSON.stringify(settings));
    notifyHomeContentUpdated(settings);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'settings', 'homepage'), settings);
    } catch (error) {
      console.error("Firebase saveHomepageSettings error:", error);
      throw error;
    }
  }
  return settings;
};


// ─── DISCOUNTS ───────────────────────────────────────────────────────────────
export const subscribeDiscounts = (callback, fallbackDiscounts = []) => {
  try {
    const local = localStorage.getItem('hok_discounts');
    if (local) {
      callback(JSON.parse(local));
    } else if (fallbackDiscounts && fallbackDiscounts.length > 0) {
      callback(fallbackDiscounts);
    }
  } catch (e) {}

  const handleLocal = (e) => {
    if (e.detail) callback(e.detail);
  };
  window.addEventListener('hok_discounts_updated', handleLocal);

  const handleStorage = (e) => {
    if (e.key === 'hok_discounts') {
      try {
        const local = localStorage.getItem('hok_discounts');
        if (local) callback(JSON.parse(local));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorage);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'discounts');
      unsubscribeFirestore = onSnapshot(colRef, (snap) => {
        const live = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (live.length > 0) {
          safeSetItem('hok_discounts', JSON.stringify(live));
          callback(live);
        }
      }, (err) => {
        console.warn("Firestore discounts onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore discounts subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_discounts_updated', handleLocal);
    window.removeEventListener('storage', handleStorage);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getDiscounts = async (fallbackDiscounts) => {
  const local = localStorage.getItem('hok_discounts');
  const localDiscounts = local ? JSON.parse(local) : null;

  if (!firebaseEnabled) {
    return localDiscounts || fallbackDiscounts;
  }
  try {
    const colRef = collection(db, 'discounts');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      if (localDiscounts && localDiscounts.length > 0) {
        return localDiscounts;
      }
      for (const disc of fallbackDiscounts) {
        try {
          await setDoc(doc(db, 'discounts', disc.code), disc);
        } catch (e) {}
      }
      safeSetItem('hok_discounts', JSON.stringify(fallbackDiscounts));
      return fallbackDiscounts;
    }
    const liveDiscounts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    safeSetItem('hok_discounts', JSON.stringify(liveDiscounts));
    return liveDiscounts;
  } catch (error) {
    console.warn("Error fetching discounts from Firebase (using local):", error);
    return localDiscounts || fallbackDiscounts;
  }
};

export const saveDiscount = async (discount) => {
  // 1. Immediately persist locally
  let updated = [];
  try {
    const local = localStorage.getItem('hok_discounts');
    const discounts = local ? JSON.parse(local) : [];
    updated = [...discounts.filter(d => d.code !== discount.code), discount];
    safeSetItem('hok_discounts', JSON.stringify(updated));
    notifyDiscountsUpdated(updated);
  } catch (e) {
    console.warn("Local storage discount save error:", e);
  }

  // 2. Persist to Firebase
  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'discounts', discount.code), discount);
      console.log(`Discount ${discount.code} saved to Firebase.`);
    } catch (error) {
      console.warn("Error saving discount to Firebase (persisted locally):", error);
    }
  }
  return discount;
};

export const deleteDiscount = async (code) => {
  // 1. Immediately update local storage
  try {
    const local = localStorage.getItem('hok_discounts');
    if (local) {
      const discounts = JSON.parse(local).filter(d => d.code !== code);
      safeSetItem('hok_discounts', JSON.stringify(discounts));
      notifyDiscountsUpdated(discounts);
    }
  } catch (e) {
    console.warn("Local storage discount delete error:", e);
  }

  // 2. Delete from Firebase
  if (firebaseEnabled) {
    try {
      await deleteDoc(doc(db, 'discounts', code));
      console.log(`Discount ${code} deleted from Firebase.`);
    } catch (error) {
      console.warn("Error deleting discount from Firebase (deleted locally):", error);
    }
  }
  return { success: true, code };
};

// ─── COUPON EXPIRATION HELPER ────────────────────────────────────────────────
export const isCouponExpired = (coupon) => {
  if (!coupon || !coupon.validUntil) return false;
  try {
    const expiry = new Date(coupon.validUntil);
    // Expiration takes effect at 23:59:59.999 on the given day
    expiry.setHours(23, 59, 59, 999);
    return Date.now() > expiry.getTime();
  } catch (e) {
    return false;
  }
};

// ─── MEDIA COMPRESSION (IMAGE & VIDEO) ─────────────────────────────────────────

// Balanced high-fidelity image optimization
export const compressImage = (file, maxWidth = 2500, quality = 1.0) => {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      return resolve(file);
    }

    // Skip vector SVGs
    if (file.type === 'image/svg+xml') {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // BALANCED APPROACH: If the image is under 1.5MB and fits within 2500px, keep it PRISTINE.
        // This preserves jewelry details while still stopping massive 10MB raw files.
        if (file.size <= 1536 * 1024 && width <= maxWidth && height <= maxWidth) {
          console.log(`[HOK Media] Image is optimal size (${(file.size / 1024).toFixed(1)} KB), skipping compression to preserve maximum quality.`);
          return resolve(file);
        }

        // Calculate proportional scale preserving aspect ratio
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(file);

        // High-precision bicubic smoothing for crisp gemstone facets and filigree
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');

        // Prefer modern WebP; fallback to JPEG or PNG
        let targetMime = 'image/jpeg';
        let targetExt = '.jpg';

        try {
          const supportsWebp = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
          if (supportsWebp && !isPng) {
            targetMime = 'image/webp';
            targetExt = '.webp';
          } else if (isPng) {
            targetMime = 'image/png';
            targetExt = '.png';
          }
        } catch (e) {
          targetMime = isPng ? 'image/png' : 'image/jpeg';
          targetExt = isPng ? '.png' : '.jpg';
        }

        // Use quality = 1.0 (100%) to prevent artifacting
        canvas.toBlob((blob) => {
          if (blob && blob.size < file.size) {
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + targetExt, {
              type: targetMime,
              lastModified: Date.now()
            });
            console.log(`[HOK Media] Large image resized gracefully: ${(file.size / 1024 / 1024).toFixed(2)} MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB (${targetMime})`);
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, targetMime, quality);
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

// High-fidelity video upload helper: preserves native MP4/WebM/MOV quality without destructive canvas re-recording
export const compressVideo = (file) => {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('video/')) {
      return resolve(file);
    }
    // Retain original video file in pristine HD quality, framerate, and audio
    console.log(`[HOK Media] Video file accepted (${(file.size / 1024 / 1024).toFixed(2)} MB), preserving native 1080p stream and audio.`);
    resolve(file);
  });
};

// ─── MEDIA UPLOAD (AUTOMATIC COMPRESSION FOR IMAGES & VIDEOS) ─────────────────
const fileToDataUrl = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(URL.createObjectURL(file));
    reader.readAsDataURL(file);
  });
};

export const uploadMedia = async (file, skipCompression = false) => {
  let fileToUpload = file;
  if (!skipCompression) {
    try {
      if (file.type.startsWith('video/')) {
        fileToUpload = await compressVideo(file);
      } else if (file.type.startsWith('image/')) {
        fileToUpload = await compressImage(file);
      }
    } catch (err) {
      console.warn("[HOK Media] Compression error, proceeding with original:", err);
    }
  } else {
    console.log(`[HOK Media] Explicitly skipping compression for: ${file.name}`);
  }

  // Always upload files directly to the server's /uploads/ directory
  // When running locally on localhost, target the live site endpoint so files are written to disk
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const uploadEndpoint = isLocal ? 'https://houseofkarnnikaraa.com/upload.php' : '/upload.php';

  try {
    const formData = new FormData();
    const fieldName = fileToUpload.type.startsWith('video/') ? 'video' : 'image';
    formData.append(fieldName, fileToUpload);
    formData.append('file', fileToUpload);

    const response = await fetch(uploadEndpoint, {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (data.status === 'success' && data.url) {
      return data.url;
    } else {
      console.warn("Server upload did not succeed:", data);
    }
  } catch (error) {
    console.warn("Upload to server failed:", error);
  }

  // Fallback ONLY for small images under 250KB
  // Multi-megabyte files CANNOT be saved as Base64 because Firestore hard-limits documents to 1MB!
  if (fileToUpload.type.startsWith('image/')) {
    if (fileToUpload.size < 250 * 1024) {
      try {
        return await fileToDataUrl(fileToUpload);
      } catch (e) {
        console.warn("Base64 generation error:", e);
      }
    } else {
      throw new Error(`Server upload failed. The selected file (${Math.round(fileToUpload.size / 1024)} KB) is too large to save in the database directly. Please check your internet connection and try again.`);
    }
  }

  throw new Error("File upload failed. Please verify your internet connection and try again.");
};

export const uploadImage = uploadMedia;

// ─── NEWSLETTER SUBSCRIBERS ──────────────────────────────────────────────────
export const subscribeNewsletterEmails = (callback) => {
  const fallback = [
    { email: 'client1@example.com', date: '12 Jul 2026', timestamp: 1783859200000 },
    { email: 'client2@gmail.com', date: '10 Jul 2026', timestamp: 1783686400000 },
    { email: 'buyer@outlook.com', date: '05 Jul 2026', timestamp: 1783254400000 }
  ];

  try {
    const local = localStorage.getItem('hok_newsletter');
    if (local) {
      callback(JSON.parse(local));
    } else {
      callback(fallback);
    }
  } catch (e) {}

  const handleLocal = (e) => {
    if (e.detail) callback(e.detail);
  };
  window.addEventListener('hok_newsletter_updated', handleLocal);

  const handleStorage = (e) => {
    if (e.key === 'hok_newsletter') {
      try {
        const local = localStorage.getItem('hok_newsletter');
        if (local) callback(JSON.parse(local));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorage);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'newsletter');
      const q = query(colRef, orderBy('timestamp', 'desc'));
      unsubscribeFirestore = onSnapshot(q, (snap) => {
        const live = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (live.length > 0) {
          safeSetItem('hok_newsletter', JSON.stringify(live));
          callback(live);
        }
      }, (err) => {
        console.warn("Firestore newsletter onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore newsletter subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_newsletter_updated', handleLocal);
    window.removeEventListener('storage', handleStorage);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const subscribeNewsletter = async (email) => {
  const item = {
    email: email.trim().toLowerCase(),
    date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
    timestamp: Date.now()
  };
  const local = localStorage.getItem('hok_newsletter');
  const list = local ? JSON.parse(local) : [
    { email: 'client1@example.com', date: '12 Jul 2026', timestamp: 1783859200000 },
    { email: 'client2@gmail.com', date: '10 Jul 2026', timestamp: 1783686400000 },
    { email: 'buyer@outlook.com', date: '05 Jul 2026', timestamp: 1783254400000 }
  ];
  if (!list.some(x => x.email === item.email)) {
    list.unshift(item);
    safeSetItem('hok_newsletter', JSON.stringify(list));
    notifyNewsletterUpdated(list);
  }

  if (firebaseEnabled) {
    try {
      const colRef = collection(db, 'newsletter');
      const q = query(colRef, where('email', '==', item.email));
      const snap = await getDocs(q);
      if (snap.empty) {
        await addDoc(colRef, item);
      }
    } catch (error) {
      console.warn("Firestore subscribeNewsletter warning (persisted locally):", error);
    }
  }
  return item;
};

export const getNewsletterEmails = async () => {
  const fallback = [
    { email: 'client1@example.com', date: '12 Jul 2026', timestamp: 1783859200000 },
    { email: 'client2@gmail.com', date: '10 Jul 2026', timestamp: 1783686400000 },
    { email: 'buyer@outlook.com', date: '05 Jul 2026', timestamp: 1783254400000 }
  ];
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_newsletter');
    return local ? JSON.parse(local) : fallback;
  }
  try {
    const colRef = collection(db, 'newsletter');
    const q = query(colRef, orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    if (snap.empty) {
      return [];
    }
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching newsletter subscribers:", error);
    return fallback;
  }
};

// ─── COLLECTIONS SETTINGS ───────────────────────────────────────────────────
export const subscribeCollectionsSettings = (callback, fallbackSettings) => {
  try {
    const local = localStorage.getItem('hok_collectionsContent');
    if (local) {
      callback(sanitizeCollections(JSON.parse(local)));
    } else if (fallbackSettings) {
      callback(sanitizeCollections(fallbackSettings));
    }
  } catch (e) {}

  const sanitizeCollections = (data) => {
    if (!data) return data;
    const clean = {};
    for (const key in data) {
      clean[key] = {
        ...data[key],
        banner: resolveMediaUrl(data[key].banner)
      };
    }
    return clean;
  };

  const handleLocal = (e) => {
    if (e.detail) callback(sanitizeCollections(e.detail));
  };
  window.addEventListener('hok_collectionsdata_updated', handleLocal);

  const handleStorage = (e) => {
    if (e.key === 'hok_collectionsContent') {
      try {
        const local = localStorage.getItem('hok_collectionsContent');
        if (local) callback(sanitizeCollections(JSON.parse(local)));
      } catch (err) {}
    }
  };
  window.addEventListener('storage', handleStorage);

  let unsubscribeFirestore = () => {};
  if (firebaseEnabled) {
    try {
      const docRef = doc(db, 'settings', 'collections');
      unsubscribeFirestore = onSnapshot(docRef, (snap) => {
        if (snap.exists()) {
          const data = sanitizeCollections(snap.data());
          safeSetItem('hok_collectionsContent', JSON.stringify(data));
          callback(data);
        }
      }, (err) => {
        console.warn("Firestore collections settings onSnapshot warning:", err);
      });
    } catch (err) {
      console.warn("Firestore collections subscription error:", err);
    }
  }

  return () => {
    window.removeEventListener('hok_collectionsdata_updated', handleLocal);
    window.removeEventListener('storage', handleStorage);
    if (typeof unsubscribeFirestore === 'function') {
      unsubscribeFirestore();
    }
  };
};

export const getCollectionsSettings = async (fallbackSettings) => {
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_collectionsContent');
    return local ? JSON.parse(local) : fallbackSettings;
  }
  try {
    const snap = await getDocs(collection(db, 'settings'));
    const docObj = snap.docs.find(d => d.id === 'collections');
    if (!docObj) {
      await setDoc(doc(db, 'settings', 'collections'), fallbackSettings);
      return fallbackSettings;
    }
    return docObj.data();
  } catch (error) {
    console.error("Error fetching collections settings from Firebase:", error);
    return fallbackSettings;
  }
};

export const saveCollectionsSettings = async (settings) => {
  try {
    safeSetItem('hok_collectionsContent', JSON.stringify(settings));
    notifyCollectionsDataUpdated(settings);
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'settings', 'collections'), settings);
    } catch (error) {
      console.warn("Firebase saveCollectionsSettings warning (persisted locally):", error);
    }
  }
  return settings;
};

// ─── ADMIN CREDENTIALS ────────────────────────────────────────────────────────
export const getAdminCredentials = async (fallbackCreds) => {
  const defaultCreds = fallbackCreds || {
    email: 'houseofkarnnikaraa@gmail.com',
    password: '#Karnnikaraa2017@2026'
  };
  if (!firebaseEnabled) {
    const local = localStorage.getItem('hok_admin_creds');
    return local ? JSON.parse(local) : defaultCreds;
  }
  try {
    const snap = await getDocs(collection(db, 'settings'));
    const docObj = snap.docs.find(d => d.id === 'admin');
    if (!docObj) {
      await setDoc(doc(db, 'settings', 'admin'), defaultCreds);
      return defaultCreds;
    }
    return docObj.data();
  } catch (error) {
    console.error("Error fetching admin credentials from Firebase:", error);
    const local = localStorage.getItem('hok_admin_creds');
    return local ? JSON.parse(local) : defaultCreds;
  }
};

export const saveAdminCredentials = async (creds) => {
  try {
    safeSetItem('hok_admin_creds', JSON.stringify(creds));
  } catch (e) {}

  if (firebaseEnabled) {
    try {
      await setDoc(doc(db, 'settings', 'admin'), creds);
    } catch (error) {
      console.warn("Firebase saveAdminCredentials warning (persisted locally):", error);
    }
  }
  return creds;
};

// ─── USER WISHLIST CLOUD & LOCAL STORAGE PERSISTENCE ──────────────────────────
export const getWishlistFromDb = async (userId) => {
  if (!userId) {
    try {
      const local = localStorage.getItem('hok_wishlist');
      return local ? JSON.parse(local) : [];
    } catch (e) {
      return [];
    }
  }

  // Check cloud Firestore for logged in user
  if (firebaseEnabled) {
    try {
      const userDocRef = doc(db, 'users', String(userId));
      const userSnap = await getDoc(userDocRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        if (Array.isArray(data.wishlist)) {
          safeSetItem(`hok_wishlist_${userId}`, JSON.stringify(data.wishlist));
          safeSetItem('hok_wishlist', JSON.stringify(data.wishlist));
          return data.wishlist;
        }
      }
    } catch (error) {
      console.warn("Error fetching wishlist from Firebase:", error);
    }
  }

  // Fallback to local storage
  try {
    const userLocal = localStorage.getItem(`hok_wishlist_${userId}`);
    if (userLocal) return JSON.parse(userLocal);
    const guestLocal = localStorage.getItem('hok_wishlist');
    return guestLocal ? JSON.parse(guestLocal) : [];
  } catch (e) {
    return [];
  }
};

export const saveWishlistToDb = async (userId, wishlist) => {
  const cleanList = Array.isArray(wishlist) ? wishlist : [];
  // 1. Always cache in localStorage for instant access & guest users
  try {
    safeSetItem('hok_wishlist', JSON.stringify(cleanList));
    if (userId) {
      safeSetItem(`hok_wishlist_${userId}`, JSON.stringify(cleanList));
    }
    notifyWishlistUpdated(cleanList);
  } catch (e) {}

  // 2. Persist to Firestore if user is authenticated
  if (userId && firebaseEnabled) {
    try {
      const userDocRef = doc(db, 'users', String(userId));
      await setDoc(userDocRef, {
        wishlist: cleanList,
        lastUpdated: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.warn("Error saving wishlist to Firebase:", error);
    }
  }
  return cleanList;
};

export const syncGuestWishlistWithUser = async (userId) => {
  if (!userId) return [];
  try {
    let guestWishlist = [];
    try {
      const saved = localStorage.getItem('hok_wishlist');
      if (saved) guestWishlist = JSON.parse(saved);
    } catch (e) {}

    const remoteWishlist = await getWishlistFromDb(userId);
    // Merge unique product IDs
    const merged = Array.from(new Set([
      ...(Array.isArray(remoteWishlist) ? remoteWishlist : []),
      ...(Array.isArray(guestWishlist) ? guestWishlist : [])
    ]));

    await saveWishlistToDb(userId, merged);
    return merged;
  } catch (error) {
    console.warn("Error syncing guest wishlist with user:", error);
    return [];
  }
};
